import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import styles from './index.module.scss';
import SignUpPanel from '../../components/Conjure/SignUp';
import handsIcon from './hands.ico';
import { CLUSTER } from '../../constants';

const BASE_PATH = CLUSTER !== 'dev' ? '/ai-ui' : '';
const OG_IMAGE = `${BASE_PATH}/static/images/conjure-og.png`;
const POSTER_IMAGE_PATH = '/static/images/poster-img.png';
const { publicRuntimeConfig = {} } = getConfig() || {};
const ASSET_PREFIX = publicRuntimeConfig.ASSET_PREFIX || '';

const joinUrl = (prefix, pathname) => {
  if (!prefix) return pathname;
  return `${prefix.replace(/\/$/, '')}${pathname}`;
};

const VIDEO_PATH = '/static/videos/bg-vid.mp4';
const VIDEO_SRCS = Array.from(
  new Set([
    joinUrl(BASE_PATH, VIDEO_PATH),
    VIDEO_PATH,
    joinUrl(ASSET_PREFIX, joinUrl(BASE_PATH, VIDEO_PATH)),
    joinUrl(ASSET_PREFIX, VIDEO_PATH),
  ])
);

const POSTER_SRCS = Array.from(
  new Set([
    joinUrl(BASE_PATH, POSTER_IMAGE_PATH),
    POSTER_IMAGE_PATH,
    joinUrl(ASSET_PREFIX, joinUrl(BASE_PATH, POSTER_IMAGE_PATH)),
    joinUrl(ASSET_PREFIX, POSTER_IMAGE_PATH),
  ])
);

class ConjureErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[Conjure] client-side exception:', error, info);
  }

  render() {
    if (this.state.error) return null;
    return this.props.children;
  }
}

function Conjure() {
  const videoRef = useRef(null);
  const [videoSrcIndex, setVideoSrcIndex] = useState(0);
  const [posterSrcIndex, setPosterSrcIndex] = useState(0);

  const videoSrc = VIDEO_SRCS[videoSrcIndex];
  const posterSrc = POSTER_SRCS[posterSrcIndex];

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // ignore autoplay failures; no text UI
      });
    }
  }, [videoSrcIndex]);

  useEffect(() => {
    if (posterSrcIndex >= POSTER_SRCS.length - 1) return;

    let cancelled = false;
    const img = new Image();
    img.onerror = () => {
      if (cancelled) return;
      setPosterSrcIndex((i) => (i < POSTER_SRCS.length - 1 ? i + 1 : i));
    };

    img.src = posterSrc;

    return () => {
      cancelled = true;
    };
  }, [posterSrcIndex, posterSrc]);

  return (
    <div className={styles.conjurePage}>
      <Head>
        <link rel='icon' href={handsIcon} sizes='any' />
        <link rel='shortcut icon' href={handsIcon} />
        <meta
          name='description'
          content='Conjure - Your camera was never meant to see everything... IOS App coming soon.'
        />
        <meta
          property='og:description'
          content='Conjure - Your camera was never meant to see everything... IOS App coming soon.'
        />
        <meta property='og:image' content={OG_IMAGE} />
        <meta name='twitter:image' content={OG_IMAGE} />
      </Head>
      <video
        key={videoSrc}
        ref={videoRef}
        className={styles.conjureVideo}
        src={videoSrc}
        poster={posterSrc}
        autoPlay
        loop
        muted
        playsInline
        preload='auto'
        aria-hidden='true'
        onError={() => {
          if (videoSrcIndex < VIDEO_SRCS.length - 1) {
            setVideoSrcIndex((i) => i + 1);
          }
        }}
      />

      <div className={styles.conjureContent}>
        <SignUpPanel />
      </div>

      <footer className={styles.privacyFooter}>
        <a href='/policies/privacy-policy'>
          PRIVACY POLICY
        </a>
      </footer>
    </div>
  );
}

export default function ConjurePage() {
  return (
    <ConjureErrorBoundary>
      <Conjure />
    </ConjureErrorBoundary>
  );
}